import { t } from "i18next";
import type { JSX } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/**
 * Signing out with recordings still on this device: keep them (hidden until
 * this account signs in again, deleted after 7 days) or delete them now.
 */
export const SignOutRecordingsDialog = ({
  open,
  count,
  busy,
  onCancel,
  onKeep,
  onDelete,
}: {
  open: boolean;
  count: number;
  busy: boolean;
  onCancel: () => void;
  onKeep: () => void;
  onDelete: () => void;
}): JSX.Element => (
  <AlertDialog
    open={open}
    onOpenChange={(value) => {
      if (!value && !busy) onCancel();
    }}
  >
    <AlertDialogContent className="w-[calc(100%-2rem)] max-w-md rounded-2xl">
      <AlertDialogHeader className="text-left">
        <AlertDialogTitle>
          {t(
            "offlineRecording.signOut.title",
            "{{count}} recording(s) not uploaded yet",
            { count },
          )}
        </AlertDialogTitle>
        <AlertDialogDescription className="text-sm leading-6">
          {t(
            "offlineRecording.signOut.description",
            "They are saved in this browser. If you keep them, they upload the next time you sign in here and are deleted after 7 days. On a shared computer, delete them or upload them first.",
          )}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
        <Button className="min-h-11 w-full" disabled={busy} onClick={onCancel}>
          {t("offlineRecording.signOut.stay", "Stay signed in")}
        </Button>
        <Button
          className="min-h-11 w-full"
          disabled={busy}
          variant="outline"
          onClick={onKeep}
        >
          {t(
            "offlineRecording.signOut.keep",
            "Keep on this device and sign out",
          )}
        </Button>
        <Button
          className="min-h-11 w-full text-error hover:text-error"
          disabled={busy}
          variant="outline"
          onClick={onDelete}
        >
          {t("offlineRecording.signOut.delete", "Delete them and sign out")}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
